/**
 * « Lien de position » envoyé au destinataire (migration 0030, edge function `partager-position`).
 *
 * L'opératrice demande au destinataire de partager SA position : la console crée la demande
 * (`creer_demande_position`, qui rend le jeton en clair UNE fois), construit le lien, et ouvre
 * WhatsApp avec un message prérempli — la personne relit et envoie elle-même, comme pour les mises
 * à jour de statut (`whatsapp.ts`). Aucun envoi automatique.
 *
 * Qui reçoit le lien : le destinataire de la course s'il a un téléphone, sinon le client — même règle
 * que l'appel et WhatsApp (`cibleAppel`). Si ce numéro est inexploitable, on le dit : pas de repli.
 */
import { cibleAppel } from './destinataire'
import { lienWhatsApp, normaliserNumero } from './whatsapp'

/** Hôte du lien (décision du 06/10/2026) : la page statique `/position` est servie sur deliver. et ops. */
export const URL_PAGE_POSITION = 'https://deliver.yiriwa-group.com/position'

/** Durée de vie du lien, en heures (défaut de `demandes_position.expires_at`). */
export const VALIDITE_LIEN_HEURES = 48

/** `https://deliver.yiriwa-group.com/position?t=<jeton>` — le jeton est hexadécimal : rien à encoder. */
export function lienPosition(jeton: string): string {
  return `${URL_PAGE_POSITION}?t=${encodeURIComponent(jeton)}`
}

export interface ContextePosition {
  /** Référence de la course (CMD-…). */
  ref: string
  /** Nom commercial de l'organisation (marque blanche). */
  marque: string
  /** Prénom ou nom du destinataire à saluer, si c'est lui qu'on joint. */
  nom?: string | null
  lien: string
}

/** Le message WhatsApp, en français, une seule source. */
export function messagePosition(c: ContextePosition): string {
  const nom = String(c.nom ?? '').replace(/[\r\n\t]+/g, ' ').replace(/ {2,}/g, ' ').trim().slice(0, 120)
  const bonjour = nom ? `Bonjour ${nom},` : 'Bonjour,'
  return (
    `${bonjour} ${c.marque} doit vous livrer un colis (réf. ${c.ref}). ` +
    `Pour que le livreur trouve l'adresse, partagez votre position en un clic : ${c.lien} ` +
    `(lien personnel, valable ${VALIDITE_LIEN_HEURES} h).`
  )
}

export type PreparationPosition =
  | { ok: true; url: string; numero: string; texte: string; destinataire: boolean }
  | { ok: false; raison: string }

/** Tout ce qu'il faut pour proposer le bouton WhatsApp : lien prêt, ou raison pour laquelle on ne peut pas. */
export function preparerDemandePosition(
  course: { recipient_name?: string | null; recipient_phone?: string | null; client_phone?: string | null },
  repliClient: string | null | undefined,
  contexte: Omit<ContextePosition, 'nom'>,
): PreparationPosition {
  const cible = cibleAppel({ ...course, client_phone: course.client_phone ?? repliClient ?? null })
  if (!cible.telephone) return { ok: false, raison: "Aucun numéro de téléphone n'est enregistré pour cette course." }
  const numero = normaliserNumero(cible.telephone)
  if (!numero) {
    return { ok: false, raison: `Le numéro « ${cible.telephone} » n'est pas exploitable : précisez l'indicatif du pays (ex. +223 75 08 36 64).` }
  }
  const nom = cible.destinataire ? (course.recipient_name ?? null) : null
  const texte = messagePosition({ ...contexte, nom })
  return { ok: true, url: lienWhatsApp(numero, texte), numero, texte, destinataire: cible.destinataire }
}
